import { useQuery } from "@tanstack/react-query";
import { Database } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import type { DataCollectionSummary } from "@/api";
import { listDataSourcesOptions } from "@/api";
import { EmptyState } from "@/components/build/EmptyState";
import { EntityListPage } from "@/components/build/EntityListPage";
import { EntityRow } from "@/components/build/EntityRow";
import { byName, useListControls } from "@/components/build/useListControls";
import { BuildWithAiButton } from "@/features/build/withAi/BuildWithAiButton";
import { DataSourceIcon, DataSourceStatusBadge } from "./dataSourceMeta";
import { useDeleteDataSource } from "./useDeleteDataSource";

const ACTIVE_STATUSES = new Set(["pending", "syncing", "indexing"]);

/** "Mine" = any membership role (same as agents). */
const isMine = (s: DataCollectionSummary) => Boolean(s.currentUserRole);

/** Build > Data sources list. Polls while any source is syncing/indexing. */
export function DataSourcesListPage() {
  const { t } = useTranslation(["build", "dataSources"]);
  const navigate = useNavigate();
  const { onDelete, dialog } = useDeleteDataSource();
  const { search, apply, listProps } = useListControls<DataCollectionSummary>({
    isMine,
    sorts: [
      { value: "name", label: t("build:list.sort.name"), compare: byName },
      // Summaries carry no createdAt; uuids are time-ordered.
      { value: "newest", label: t("build:list.sort.newest"), compare: (a, b) => b.uuid.localeCompare(a.uuid) },
    ],
  });

  const query = useQuery({
    ...listDataSourcesOptions({ query: { page_size: 50 } }),
    refetchInterval: (q) => {
      const items = q.state.data?.data ?? [];
      return items.some((s) => ACTIVE_STATUSES.has(s.status)) ? 5000 : false;
    },
  });
  const sources = apply(query.data?.data);

  return (
    <EntityListPage
      title={t("dataSources:title")}
      subtitle={t("dataSources:subtitle")}
      newLabel={t("dataSources:new")}
      onNew={() => navigate("/build/data-sources/new")}
      headerActions={<BuildWithAiButton entity="data_source" />}
      {...listProps}
    >
      {sources.map((source) => (
        <EntityRow
          key={source.uuid}
          icon={<DataSourceIcon sourceType={source.sourceType} />}
          name={source.name}
          description={source.description}
          meta={<DataSourceStatusBadge status={source.status} />}
          onOpen={() => navigate(`/build/data-sources/${source.uuid}`)}
          menuItems={[
            { label: t("build:menu.edit"), onSelect: () => navigate(`/build/data-sources/${source.uuid}`) },
            { label: t("build:menu.delete"), danger: true, onSelect: () => onDelete(source) },
          ]}
        />
      ))}
      {sources.length === 0 &&
        (search ? (
          <EmptyState title={t("build:list.noResultsTitle")} body={t("build:list.noResultsBody", { query: search })} />
        ) : (
          <EmptyState
            icon={<Database size={32} />}
            title={t("dataSources:empty.title")}
            body={t("dataSources:empty.body")}
          />
        ))}
      {dialog}
    </EntityListPage>
  );
}
